// FRONT END PRODUCT CONFIG
import { frontEnvNumber } from "src/services/runtimeFrontConfig";

/** Parse a non-negative USD amount from process.env (same pattern as REACT_APP_SUB_*_CREDITS). */
function readReactAppPriceUsd(envKey: string, fallback: number): number {
  const raw = process.env[envKey];
  if (raw === undefined || raw === "") return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

/** Display / checkout math (Stripe amounts still come from Price IDs on the server). */
export const SUB_BASIC_MONTHLY_PRICE_USD = readReactAppPriceUsd("REACT_APP_SUB_BASIC_MONTHLY_PRICE", 4.99);
export const SUB_MONTHLY_PRICE_USD = readReactAppPriceUsd("REACT_APP_SUB_MONTHLY_PRICE", 9.99);
export const SUB_ANNUAL_PRO_PRICE_USD = readReactAppPriceUsd("REACT_APP_SUB_ANNUAL_PRO_PRICE", 79.99);
export const SUB_SEASON_PASS_TIER_1_PRICE_USD = readReactAppPriceUsd("REACT_APP_SUB_SEASON_PASS_TIER_1_PRICE", 39.99);
export const SUB_SEASON_PASS_TIER_2_PRICE_USD = readReactAppPriceUsd("REACT_APP_SUB_SEASON_PASS_TIER_2_PRICE", 24.99);
export const SUB_SEASON_PASS_TIER_3_PRICE_USD = readReactAppPriceUsd("REACT_APP_SUB_SEASON_PASS_TIER_3_PRICE", 14.99);
export const SUB_SEASON_PASS_TIER_4_PRICE_USD = readReactAppPriceUsd("REACT_APP_SUB_SEASON_PASS_TIER_4_PRICE", 0);
export const BOOSTER_STARTER_PRICE_USD = readReactAppPriceUsd("REACT_APP_BOOSTER_STARTER_PRICE", 1.99);
export const BOOSTER_POWER_PRICE_USD = readReactAppPriceUsd("REACT_APP_BOOSTER_POWER_PRICE", 9.99);

export interface ProductConfig {
  productId: string;
  subscription: string; // 'standard', 'premium', 'booster_5', 'booster_25'
  creditsAmount: number;
  isSubscription: boolean;
  price: number;
  displayName: string;
}

/**
 * Helper function to get Season Pass price based on seasonPassPrice value
 * @param seasonPassPrice - "1", "2", "3", or "4"
 * @returns Price in dollars
 */
export function getSeasonPassPrice(seasonPassPrice: string): number {
  if (seasonPassPrice === "1") return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_1_PRICE", 39.99);
  if (seasonPassPrice === "2") return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_2_PRICE", 24.99);
  if (seasonPassPrice === "3") return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_3_PRICE", 14.99);
  if (seasonPassPrice === "4") return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_4_PRICE", 0);
  return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_1_PRICE", 39.99); // default
}

function seasonPassTierCreditsAmount(tier: string): number {
  if (tier === "2") return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_2_CREDITS", 1600);
  if (tier === "3") return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_3_CREDITS", 800);
  return frontEnvNumber("REACT_APP_SUB_SEASON_PASS_TIER_1_CREDITS", 2400);
}

export const PRODUCTS_CONFIG: Record<string, ProductConfig> = {
  // --------------------
  // Subscriptions and Boosters (OLDS)
  league_connect_standard_monthly: {
    productId: "league_connect_standard_monthly",
    subscription: "standard",
    creditsAmount: 40,
    isSubscription: true,
    price: 9.99,
    displayName: "Standard",
  },
  league_connect_premium_monthly: {
    productId: "league_connect_premium_monthly",
    subscription: "premium",
    creditsAmount: 100,
    isSubscription: true,
    price: 19.99,
    displayName: "Premium",
  },
  // One-time purchases (Boosters)
  league_connect_boost_5: {
    productId: "league_connect_boost_5",
    subscription: "booster_5",
    creditsAmount: 5,
    isSubscription: false,
    price: 2.99,
    displayName: "Booster 5",
  },
  league_connect_boost_25: {
    productId: "league_connect_boost_25",
    subscription: "booster_25",
    creditsAmount: 25,
    isSubscription: false,
    price: 9.99,
    displayName: "Booster 25",
  },
  // --------------------
  // New plans from UI design
  league_connect_free: {
    productId: "league_connect_free",
    subscription: "free",
    creditsAmount: 0,
    isSubscription: false,
    price: 0.0,
    displayName: "Free",
  },
  league_connect_basic_monthly: {
    productId: "league_connect_basic_monthly",
    subscription: "basic_monthly",
    creditsAmount: Number(process.env.REACT_APP_SUB_BASIC_MONTHLY_CREDITS) || 100,
    isSubscription: true,
    price: SUB_BASIC_MONTHLY_PRICE_USD,
    displayName: "Basic Monthly",
  },
  league_connect_monthly: {
    productId: "league_connect_monthly",
    subscription: "monthly",
    creditsAmount: Number(process.env.REACT_APP_SUB_MONTHLY_CREDITS) || 400,
    isSubscription: true,
    price: SUB_MONTHLY_PRICE_USD,
    displayName: "Monthly",
  },
  league_connect_season_pass: {
    productId: "league_connect_season_pass",
    subscription: "season_pass",
    creditsAmount: Number(process.env.REACT_APP_SUB_SEASON_PASS_TIER_1_CREDITS) || 2400,
    isSubscription: true,
    // Note: price is now calculated dynamically using getSeasonPassPrice()
    // For backward compatibility, we still calculate a default value here using '1' (Pre-Season)
    // but it should be overridden when using getProductConfig with seasonPassPrice
    price: getSeasonPassPrice("1"), // Default to '1' (Pre-Season) for backward compatibility
    displayName: "Season Pass",
  },
  league_connect_annual_pro: {
    productId: "league_connect_annual_pro",
    subscription: "annual_pro",
    creditsAmount: Number(process.env.REACT_APP_SUB_ANNUAL_PRO_CREDITS) || 12000,
    isSubscription: true,
    price: SUB_ANNUAL_PRO_PRICE_USD,
    displayName: "Annual Pro",
  },

  // New boosters from UI design
  league_connect_starter_booster: {
    productId: "league_connect_boost_starter",
    subscription: "booster_starter",
    creditsAmount: Number(process.env.REACT_APP_BOOSTER_STARTER_CREDITS) || 30,
    isSubscription: false,
    price: BOOSTER_STARTER_PRICE_USD,
    displayName: "Starter Booster",
  },
  league_connect_power_booster: {
    productId: "league_connect_boost_power",
    subscription: "booster_power",
    creditsAmount: Number(process.env.REACT_APP_BOOSTER_POWER_CREDITS) || 250,
    isSubscription: false,
    price: BOOSTER_POWER_PRICE_USD,
    displayName: "Power Booster",
  },
};

/**
 * Get product configuration by product ID
 * @param productId - The product ID
 * @param seasonPassPrice - Optional season pass price value ("1", "2", "3", or "4") for season_pass products
 * @returns ProductConfig or null
 */
export function getProductConfig(productId: string, seasonPassPrice?: string): ProductConfig | null {
  // Config keys can differ from productId (e.g. league_connect_power_booster vs league_connect_boost_power)
  const config = PRODUCTS_CONFIG[productId] || Object.values(PRODUCTS_CONFIG).find((p) => p.productId === productId);
  if (!config) return null;

  let merged: ProductConfig = config;

  if (productId === "league_connect_basic_monthly") {
    merged = {
      ...config,
      creditsAmount: frontEnvNumber("REACT_APP_SUB_BASIC_MONTHLY_CREDITS", 100),
      price: frontEnvNumber("REACT_APP_SUB_BASIC_MONTHLY_PRICE", 4.99),
    };
  }

  if (productId === "league_connect_monthly") {
    merged = {
      ...config,
      creditsAmount: frontEnvNumber("REACT_APP_SUB_MONTHLY_CREDITS", 400),
      price: frontEnvNumber("REACT_APP_SUB_MONTHLY_PRICE", 14.99),
    };
  }

  if (productId === "league_connect_annual_pro") {
    merged = {
      ...config,
      creditsAmount: frontEnvNumber("REACT_APP_SUB_ANNUAL_PRO_CREDITS", 12000),
      price: frontEnvNumber("REACT_APP_SUB_ANNUAL_PRO_PRICE", 99.99),
    };
  }

  if (productId === "league_connect_boost_starter") {
    merged = {
      ...config,
      creditsAmount: frontEnvNumber("REACT_APP_BOOSTER_STARTER_CREDITS", 30),
      price: frontEnvNumber("REACT_APP_BOOSTER_STARTER_PRICE", 1.99),
    };
  }

  if (productId === "league_connect_boost_power") {
    merged = {
      ...config,
      creditsAmount: frontEnvNumber("REACT_APP_BOOSTER_POWER_CREDITS", 250),
      price: frontEnvNumber("REACT_APP_BOOSTER_POWER_PRICE", 14.99),
    };
  }

  if (productId === "league_connect_season_pass") {
    const tier = seasonPassPrice || "1";
    merged = {
      ...merged,
      price: getSeasonPassPrice(tier),
      creditsAmount: seasonPassTierCreditsAmount(tier),
    };
  }

  return merged;
}
